import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography, Button } from '@mui/material';
import { PacmanLoader } from 'react-spinners';
import { FnPageLogoDown } from '../Home/Index';
import BackHomeButton from '../../../components/BackHomeButton';
import { isAuthenticatedByCache as checkAuthCache } from '../../../Auth/Signin/Signin';
import useAuthCache from '../../../hooks/useAuthCache';
import {
  AUTH0_APP_STATE,
  formatAuth0Error,
  isAuth0PopupClosed,
} from '../../../config/auth0';

/**
 * Login embutido na plataforma via Auth0 popup (Universal Login não pode
 * ser carregado em iframe). Erros (bloqueio, sem permissão, etc.) aparecem
 * fora do popup, nesta tela. Após sucesso → /private.
 */
export default function Login() {
  const history = useHistory();
  const location = useLocation();
  const { isLoading: auth0CacheLoading } = useAuthCache();
  const {
    isLoading,
    error: auth0Error,
    loginWithPopup,
    isAuthenticated: auth0Authenticated,
  } = useAuth0();

  const isAuthenticated = checkAuthCache() || auth0Authenticated;
  const [loginError, setLoginError] = useState(null);
  const [loginInfo, setLoginInfo] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Erros vindos da query (redirect Auth0 → /private?error=… → /login?error=…)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromQuery = formatAuth0Error(params.get('error'));
    if (fromQuery) {
      setLoginError(fromQuery);
      setLoginInfo(null);
      history.replace('/login');
    }
  }, [location.search, history]);

  // Erro do SDK Auth0 (ex.: após redirect legado)
  useEffect(() => {
    const fromSdk = formatAuth0Error(auth0Error);
    if (fromSdk) {
      setLoginError(fromSdk);
      setLoginInfo(null);
    }
  }, [auth0Error]);

  const handleLogin = useCallback(async () => {
    setLoginError(null);
    setLoginInfo(null);
    setIsLoggingIn(true);
    try {
      await loginWithPopup({
        authorizationParams: {
          prompt: 'login',
        },
        appState: AUTH0_APP_STATE,
      });
      history.replace('/private');
    } catch (err) {
      if (isAuth0PopupClosed(err)) {
        setLoginInfo(formatAuth0Error(err));
      } else {
        const message = formatAuth0Error(err);
        if (message) {
          setLoginError(message);
        }
      }
    } finally {
      setIsLoggingIn(false);
    }
  }, [loginWithPopup, history]);

  const isStillLoading = isLoading || auth0CacheLoading;

  if (isStillLoading || isAuthenticated) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#262626',
        }}
      >
        <BackHomeButton />
        <FnPageLogoDown>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <Typography sx={{ color: 'primary.main' }}>Carregando</Typography>
            <PacmanLoader
              color="#ffc356"
              size={20}
              aria-label="Loading Spinner"
              data-testid="loader"
            />
          </Box>
        </FnPageLogoDown>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#262626',
      }}
    >
      <BackHomeButton />
      <FnPageLogoDown>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: '16px',
            px: 2,
          }}
        >
          {/* Avisos/erros fora do popup Auth0 */}
          {loginInfo && (
            <Box
              role="status"
              sx={{
                width: '100%',
                maxWidth: 360,
                px: 2,
                py: 1.5,
                border: '1px solid rgba(255, 195, 86, 0.55)',
                borderRadius: 1,
                backgroundColor: 'rgba(255, 195, 86, 0.1)',
              }}
            >
              <Typography
                sx={{ color: '#ffc356', textAlign: 'center', fontSize: 14 }}
              >
                {loginInfo}
              </Typography>
            </Box>
          )}

          {loginError && (
            <Box
              role="alert"
              sx={{
                width: '100%',
                maxWidth: 360,
                px: 2,
                py: 1.5,
                border: '1px solid',
                borderColor: 'error.main',
                borderRadius: 1,
                backgroundColor: 'rgba(211, 47, 47, 0.12)',
              }}
            >
              <Typography
                sx={{ color: 'error.main', textAlign: 'center', fontSize: 14 }}
              >
                {loginError}
              </Typography>
            </Box>
          )}

          {/* Painel que dispara o login embutido (popup Auth0) */}
          <Box
            sx={{
              width: '100%',
              maxWidth: 360,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              px: 3,
              py: 4,
              border: '1px solid rgba(255, 195, 86, 0.35)',
              borderRadius: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.35)',
            }}
          >
            <Typography
              sx={{
                color: '#ffc356',
                fontSize: 18,
                fontWeight: 600,
                letterSpacing: '0.04em',
              }}
            >
              Área privada
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: 13,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Entre com sua conta Google. A autenticação abre na plataforma
              (popup), em caso de sucesso, você segue para área privada.
            </Typography>

            <Button
              variant="outlined"
              onClick={handleLogin}
              disabled={isLoggingIn}
              sx={{ minWidth: 200, mt: 1 }}
            >
              {isLoggingIn ? 'Abrindo login…' : 'Entrar'}
            </Button>

            {isLoggingIn && (
              <PacmanLoader color="#ffc356" size={14} />
            )}
          </Box>
        </Box>
      </FnPageLogoDown>
    </Box>
  );
}
